import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { DrawerControl, ModalControl } from "doshx_controls_web";
import { Spinner } from "@heroui/react";
import Logo from "../assets/scee_logo.png";
import { HomePage } from "./pages/home/home";
import { AboutUsPage } from "./pages/about_us";
import { ContactUsPage } from "./pages/contact_us";
import { ShopPage } from "./pages/shop/shop";
import { BasketPage } from "./pages/basket/basket";
import { ProfilePage } from "./pages/profile/profile";
import { useSelector } from "react-redux";
import { setContentModal, setDrawerOpen, setDrawerPage, setMainModal } from "../reducers/slices/main";
import { setBasket } from "../reducers/slices/basket/basket";
import { ProductPage } from "./pages/shop/product/product";
import { CheckoutPage } from "./pages/checkout/checkout";
import { OutcomePage } from "./pages/outcome";
import { setUser, REMEMBER_ME_MAX_AGE_SECONDS } from "../reducers/slices/user";
import { ForgotPasswordPage } from "./pages/forgot_password";
import { searchProducts } from "../api/calls/product/search_products";
import { setSelectedBranch } from "../reducers/slices/selection";
import { getRootPath } from "../helpers/getRootPath";
import Cookie from "universal-cookie";
import { OrdersPage } from "./pages/orders/orders";
import { OrderDetailsPage } from "./pages/orders/details";
import { TermsAndConditionsPage } from "./pages/terms/terms_and_conditions_page";
import { PrivacyPolicyPage } from "./pages/privacy_policy";
import { get_basket_thunk } from "../reducers/slices/basket/thunks/get_basket_thunk";
import { HotDealsPage } from "./pages/hot_deals";
import { ReturnPolicyPage } from "./pages/return_policy";
// import { AuthPage } from "./pages/auth";
import { CustomTopHeaderControl } from "../controls/TopHeader";
import { AuthSidebar } from "../controls/auth-sidebar/auth-sidebar";
import { AuthPage } from "./pages/auth/auth";
import { Footer } from "../components/footer/footer";
import { AccountPage } from "./pages/account/account";
import { AddressesPage } from "./pages/addresses/addresses";
import { ScrollToTop } from "../components/scroll_to_top";
import { client_token } from "../api/calls/client/client";
import { all_branches } from "../api/calls/client/all_branches";
import { refresh_user_token } from "../api/calls/user/refresh_user_token";
export const MainScreen = () => {
    const cookie = new Cookie();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const rootPath = getRootPath(location);
    const { pathname } = location;
    // const [drawerPageName, setDrawerPageName] = useState<"login" | "signup" | "forgot" | "profile">("login");
    const [searchTerm, setSearchTerm] = useState("");
    const [searchPredictions, setSearchPredictions] = useState([]);
    const [subscribingEmail, setSubscribingEmail] = useState("");
    const [subscribingEmailError, setSubscribingEmailError] = useState("");
    const [isBusySubscribing, setIsBusySubscribing] = useState(false);
    const { main } = useSelector((state) => state.mainState);
    const { user } = useSelector((state) => state.userState);
    const { basket } = useSelector((state) => state.basketState);
    const isAuthRoute = useMemo(() => rootPath === "/login" || rootPath === "/signup" || rootPath === "/forgot-password", [rootPath]);
    const onNewsletterSubmitted = useCallback((data) => {
        console.log(data);
    }, []);
    useEffect(() => {
        document.title = process.env.APP_NAME ?? "";
    }, []);
    useEffect(() => {
        setUser({ ...user, is_loading: true });
        // Get one branch for checkout purposes.
        all_branches().then((response) => {
            if (response.success) {
                dispatch(setSelectedBranch(response.content?.branch_list[0] ?? null));
            }
            else {
                dispatch(setMainModal({
                    title: "Could not get branches!",
                    messages: [response.message],
                }));
            }
        });
        //Get user in the cookie and set it in the store
        const userStateCookie = cookie.get("user_state");
        const userTokenCookie = cookie.get("user_token");
        const userRefreshTokenCookie = cookie.get("user_refresh_token");
        // On every load, exchange the user refresh token for a fresh access token
        // so user-specific calls (e.g. addresses) don't fail with an expired JWT.
        // If the refresh itself fails (e.g. refresh token expired), the only recovery is
        // a fresh sign-in — clear stale cookies and prompt login.
        if (userRefreshTokenCookie) {
            refresh_user_token(userRefreshTokenCookie).then((response) => {
                // Refresh returns the same shape as v2 sign-in: content.user_token.{token, refresh_token}
                const newToken = response?.content?.user_token?.token ?? response?.content?.token;
                const newRefreshToken = response?.content?.user_token?.refresh_token ?? response?.content?.refresh_token;
                if (response?.success && newToken) {
                    const opts = {
                        path: "/",
                        sameSite: "lax",
                        secure: window.location.protocol === "https:",
                        maxAge: REMEMBER_ME_MAX_AGE_SECONDS,
                    };
                    cookie.set("user_token", newToken, opts);
                    if (newRefreshToken)
                        cookie.set("user_refresh_token", newRefreshToken, opts);
                }
                else {
                    console.error("Failed to refresh user token:", response?.message);
                    cookie.remove("user_state", { path: "/" });
                    cookie.remove("user_token", { path: "/" });
                    cookie.remove("user_refresh_token", { path: "/" });
                    cookie.remove("client_refresh_token", { path: "/" });
                    dispatch(setUser({
                        user: { _id: undefined, email: "", password: "", name: undefined, surname: undefined, remember_me: true },
                        token: "",
                        is_loading: false,
                    }));
                    dispatch(setBasket(null));
                    dispatch(setDrawerPage("login"));
                    dispatch(setDrawerOpen(true));
                }
            });
        }
        client_token().then((response) => {
            const baseUserObj = {
                ...user,
                ...userStateCookie,
                is_loading: false,
            };
            if (response.success) {
                dispatch(setUser({
                    ...baseUserObj,
                    token: response.content?.token ?? "",
                    client_currency: response.content?.client_currency ?? "A$",
                    sell_out_of_stock: response.content?.stock_manager?.sell_out_of_stock ?? false,
                }));
            }
            else {
                // Even when client_token fails, keep the user signed in via the saved cookies
                // so subsequent retries can use the auth token from sign-in.
                dispatch(setUser({ ...baseUserObj, token: userTokenCookie ?? baseUserObj.token ?? "" }));
                console.error("Failed to fetch client token:", response.message);
            }
        });
        // if (userStateCookie) {
        //   dispatch(setUser(userStateCookie));
        // }
    }, []);
    useEffect(() => {
        if (user.user._id) {
            dispatch(get_basket_thunk());
        }
    }, [user.user?._id]);
    useEffect(() => {
        const delayFn = setTimeout(() => {
            if (searchTerm) {
                searchProducts({
                    search_string: searchTerm,
                }).then((response) => {
                    // console.log("searchProducts response", response);
                    setSearchPredictions(response.content);
                });
            }
        }, 1000);
        return () => clearTimeout(delayFn);
    }, [searchTerm]);
    const openAccountPage = () => {
        if (user?.user._id) {
            dispatch(setDrawerPage("profile"));
            // navigate(`${rootPath}/profile`);
        }
        else {
            dispatch(setDrawerPage("login"));
            // navigate(`${location.pathname}/login`);
        }
        dispatch(setDrawerOpen(true));
    };
    const redirectToSocialMediaPage = (link) => {
        // 👇️ directly change the active URL to navigate
        if (link) {
            window.open(link, "_blank");
        }
    };
    return user.is_loading ? (React.createElement(Spinner, { color: "primary", size: "lg", className: "w-full h-screen p-4" })) : (React.createElement("div", { className: "pt-[11.3rem] lg:pt-[7.2rem]" },
        React.createElement("div", { className: "bg-secondary" },
            React.createElement(CustomTopHeaderControl, { logo: { image: Logo, size: "large", onClick: () => navigate("/") }, onAccountClick: () => {
                    openAccountPage();
                }, onCartClick: () => {
                    if (user.user?._id) {
                        navigate("/checkout");
                    }
                    else {
                        dispatch(setDrawerPage("login"));
                        dispatch(setDrawerOpen(true));
                    }
                }, onSearch: (query) => {
                    if (query) {
                        navigate(`/shop?search=${query}`);
                    }
                    else {
                        navigate("/shop");
                    }
                }, menu: {
                    textColor: "text-bborders-dark/25",
                    dropdownBackgroundColor: "bg-secondary",
                    dropdownTextColor: "text-primary",
                    menuItems: [
                        {
                            label: "Home",
                            onClick: () => {
                                navigate("/home");
                            },
                        },
                        {
                            label: "About Us",
                            onClick: () => {
                                navigate("/about-us");
                            },
                        },
                        {
                            label: "Contact Us",
                            onClick: () => {
                                navigate("/contact-us");
                            },
                        },
                        {
                            backgroundColor: "bg-primary",
                            textColor: "text-white",
                            label: "Shop Now",
                            iconColor: "text-primary",
                            onClick: () => {
                                navigate("/shop");
                                if (pathname == "/shop/categories") {
                                    navigate(0);
                                }
                            },
                        },
                    ],
                } })),
        React.createElement(DrawerControl, { open: main.isDrawerOpen, close: {
                backgroundColor: "bg-primary ml-12",
            }, toggleOpen: (v) => {
                dispatch(setDrawerOpen(v));
            } }, (() => React.createElement(AuthSidebar, null))()),
        React.createElement("div", { className: isAuthRoute ? "md:pt-28 pt-48 pb-10" : "pb-10" },
            React.createElement(ScrollToTop, null),
            React.createElement(Routes, null,
                React.createElement(Route, { index: true, element: React.createElement(Navigate, { to: `/home` }) }),
                React.createElement(Route, { path: `/home/*`, element: React.createElement(HomePage, null) }),
                React.createElement(Route, { path: `/about-us/*`, element: React.createElement(AboutUsPage, null) }),
                React.createElement(Route, { path: `/contact-us/*`, element: React.createElement(ContactUsPage, null) }),
                React.createElement(Route, { path: `/shop/:categoryId?/:categoryName?`, element: React.createElement(ShopPage, null) }),
                React.createElement(Route, { path: `/product/:productId`, element: React.createElement(ProductPage, null) }),
                React.createElement(Route, { path: `/basket/*`, element: React.createElement(BasketPage, null) }),
                React.createElement(Route, { path: `/checkout/*`, element: React.createElement(CheckoutPage, null) }),
                React.createElement(Route, { path: `/hot-deals/*`, element: React.createElement(HotDealsPage, null) }),
                React.createElement(Route, { path: `/outcome/:actionType/:paymentIdentifier`, element: React.createElement(OutcomePage, null) }),
                React.createElement(Route, { path: `/orders/*`, element: React.createElement(OrdersPage, null) }),
                React.createElement(Route, { path: `/order/details/:orderId`, element: React.createElement(OrderDetailsPage, null) }),
                React.createElement(Route, { path: `/terms-and-conditions/*`, element: React.createElement(TermsAndConditionsPage, null) }),
                React.createElement(Route, { path: `/privacy-policy/*`, element: React.createElement(PrivacyPolicyPage, null) }),
                React.createElement(Route, { path: `/return-policy/*`, element: React.createElement(ReturnPolicyPage, null) }),
                React.createElement(Route, { path: `/auth/*`, element: React.createElement(AuthPage, null) }),
                React.createElement(Route, { path: `/account-details`, element: React.createElement(AccountPage, null) }),
                React.createElement(Route, { path: `/addresses`, element: React.createElement(AddressesPage, null) }),
                React.createElement(Route, { path: `/forgot-password`, element: React.createElement(ForgotPasswordPage, null) }),
                React.createElement(Route, { path: `/profile`, element: React.createElement(ProfilePage, null) }))),
        React.createElement(Footer, { onNewsletterSubmitted: onNewsletterSubmitted }),
        main.modal && (React.createElement(ModalControl, { title: main.modal.title, open: true, onClose: () => {
                // setIsOpen(false);
                dispatch(setMainModal(null));
            } },
            React.createElement("div", { className: "p-4" }, main.modal.messages.map((message, index) => (React.createElement("div", { key: `modal-message-${index}` }, message)))))),
        main.contentModal && (React.createElement(ModalControl, { title: main.contentModal.title, open: true, onClose: () => {
                // setIsOpen(false);
                dispatch(setContentModal(null));
            } },
            React.createElement("div", { className: "p-4" }, main.contentModal.content)))));
};
